import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../utils/cn';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPreviousPage: () => void;
  onNextPage: () => void;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  onPreviousPage,
  onNextPage,
}) => {
  if (totalPages <= 1) return null;

  return (
    <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="text-sm text-gray-700">
          Page {currentPage} of {totalPages}
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={onPreviousPage}
            disabled={currentPage === 1}
            className={cn(
              'inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg border',
              currentPage === 1
                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400'
            )}
          >
            <ChevronLeft size={16} className="mr-1" />
            Previous
          </button>

          {/* Page Numbers */}
          <div className="hidden sm:flex items-center space-x-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(page => {
                return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
              })
              .map((page, index, array) => {
                const showEllipsis = index > 0 && page - array[index - 1] > 1;

                return (
                  <React.Fragment key={page}>
                    {showEllipsis && <span className="px-2 py-1 text-gray-500">...</span>}
                    <button
                      onClick={() => onPageChange(page)}
                      className={cn(
                        'px-3 py-2 text-sm font-medium rounded-lg border transition-colors',
                        page === currentPage
                          ? 'bg-primary-600 text-white border-primary-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                      )}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                );
              })}
          </div>

          {/* Mobile Page Info */}
          <div className="sm:hidden text-sm text-gray-600 px-2">
            {currentPage} / {totalPages}
          </div>

          <button
            onClick={onNextPage}
            disabled={currentPage === totalPages}
            className={cn(
              'inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg border',
              currentPage === totalPages
                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400'
            )}
          >
            Next
            <ChevronRight size={16} className="ml-1" />
          </button>
        </div>
      </div>
    </div>
  );
};
